import { useState, useEffect, useRef } from 'react'
import api from '../lib/api'
import TitanTrackRecord from './TitanTrackRecord'
import './TitansBattle.css'

const TITANS = {
  grok: {
    name: 'GROK',
    role: 'The Contrarian',
    icon: '⚡',
    color: '#d33682',
    tagline: 'Chaos finds clarity'
  },
  opus: {
    name: 'OPUS',
    role: 'The Judge',
    icon: '⚖️',
    color: '#d4af37',
    tagline: 'Truth through synthesis'
  }
}

export default function TitansBattle({ query: initialQuery, onComplete, onVote }) {
  const [query, setQuery] = useState(initialQuery || '')
  const [battleStarted, setBattleStarted] = useState(!!initialQuery)
  const [phase, setPhase] = useState(initialQuery ? 'intro' : 'input') // input, intro, opening, rebuttal, closing, voting, result
  const [rounds, setRounds] = useState([])
  const [currentRound, setCurrentRound] = useState(0)
  const [debateId, setDebateId] = useState(null)
  const [grokScore, setGrokScore] = useState(0)
  const [opusScore, setOpusScore] = useState(0)
  const [communityVotes, setCommunityVotes] = useState(null)
  const [userVote, setUserVote] = useState(null)
  const [winner, setWinner] = useState(null)
  const [typing, setTyping] = useState({ grok: false, opus: false })
  const [error, setError] = useState(null)
  const [useRealApi, setUseRealApi] = useState(true)
  const battleRef = useRef(null)
  const wsRef = useRef(null)

  // Fallback mock data for when API is unavailable
  const mockBattle = {
    query: query || "Will NVDA hit $200 by Q2 2025?",
    rounds: [
      {
        phase: 'opening',
        grok: {
          position: 'BULLISH',
          argument: "The AI capex cycle is just getting started. Every hyperscaler is racing to build out infrastructure. NVDA's data center revenue grew 409% YoY. The H100 has no real competition until late 2025. Jensen's sandbagging guidance like always.",
          confidence: 85
        },
        opus: {
          position: 'BEARISH',
          argument: "Priced for perfection at 65x forward P/E. AMD MI300X gaining traction. Custom silicon from Google TPU, Amazon Trainium eating into TAM. Blackwell delays already announced. Insider selling has accelerated. Mean reversion is inevitable.",
          confidence: 72
        }
      },
      {
        phase: 'rebuttal',
        grok: {
          argument: "AMD MI300X? Please. Their software stack is years behind CUDA. Google and Amazon chips are for their own workloads - they're not competing for enterprise. Blackwell 'delays' are FUD - H100 demand is so strong they can't ship fast enough.",
          confidence: 88
        },
        opus: {
          argument: "CUDA moat is real but narrowing. PyTorch 2.0 abstracts hardware. Inference market commoditizing fast. The $200 target implies 40% upside from here - that requires multiple expansion in a rate-cutting environment. Unlikely.",
          confidence: 75
        }
      },
      {
        phase: 'closing',
        grok: {
          argument: "Bottom line: sovereign AI spending hasn't even started. When governments realize AI is a national security priority, NVDA becomes defense contractor meets Big Tech. $200 is conservative. I'm calling $250.",
          confidence: 90,
          finalPosition: 'STRONG BUY'
        },
        opus: {
          argument: "The bear case isn't about NVDA being a bad company - it's about expectations. At $200, you need everything to go right. One earnings miss, one competitor breakthrough, one macro shock - and you get -30% fast. Risk/reward skewed negative.",
          confidence: 78,
          finalPosition: 'HOLD'
        }
      }
    ]
  }

  // Start battle - try real API first, fallback to mock
  useEffect(() => {
    if (!battleStarted || !query) return

    const startBattle = async () => {
      setPhase('intro')
      await sleep(1500) // Intro animation

      if (useRealApi) {
        try {
          // Start Titans battle via API
          const result = await api.deliberateTitans(query)
          setDebateId(result.debate_id)

          // Connect to WebSocket for streaming rounds
          wsRef.current = api.connectTitansWs(
            result.debate_id,
            // onRound - called for each round
            (round) => {
              setTyping({ grok: true, opus: true })
              setTimeout(() => {
                setRounds(prev => [...prev, round])
                setCurrentRound(prev => prev + 1)
                setPhase(round.phase)
                setTyping({ grok: false, opus: false })
              }, 1500)
            },
            // onComplete - battle finished
            (data) => {
              setPhase('voting')
              setGrokScore(data.grok_total || 0)
              setOpusScore(data.opus_total || 0)
            },
            // onError
            (err) => {
              console.warn('WebSocket error, falling back to mock:', err)
              setUseRealApi(false)
              runMockBattle()
            }
          )
        } catch (err) {
          console.warn('API error, falling back to mock:', err)
          setUseRealApi(false)
          runMockBattle()
        }
      } else {
        runMockBattle()
      }
    }

    const runMockBattle = async () => {
      setPhase('opening')
      setRounds([mockBattle.rounds[0]])
      simulateTyping('grok', 2000)
      await sleep(2500)
      simulateTyping('opus', 2000)
      await sleep(3000)

      setPhase('rebuttal')
      setCurrentRound(1)
      setRounds(prev => [...prev, mockBattle.rounds[1]])
      simulateTyping('grok', 1500)
      await sleep(2000)
      simulateTyping('opus', 1500)
      await sleep(2500)

      setPhase('closing')
      setCurrentRound(2)
      setRounds(prev => [...prev, mockBattle.rounds[2]])
      simulateTyping('grok', 1500)
      await sleep(2000)
      simulateTyping('opus', 1500)
      await sleep(2500)

      setPhase('voting')
    }

    startBattle()

    // Cleanup WebSocket on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [battleStarted, query])

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

  const handleStartBattle = (e) => {
    e.preventDefault()
    if (!query.trim()) return
    setBattleStarted(true)
  }

  const handleNewBattle = () => {
    setBattleStarted(false)
    setPhase('input')
    setRounds([])
    setCurrentRound(0)
    setDebateId(null)
    setGrokScore(0)
    setOpusScore(0)
    setCommunityVotes(null)
    setUserVote(null)
    setWinner(null)
    setError(null)
    setQuery('')
  }

  const simulateTyping = (titan, duration) => {
    setTyping(prev => ({ ...prev, [titan]: true }))
    setTimeout(() => {
      setTyping(prev => ({ ...prev, [titan]: false }))
    }, duration)
  }

  const handleVote = async (titan) => {
    if (userVote) return
    setUserVote(titan)

    // Submit vote to API if we have a debate ID
    if (debateId) {
      try {
        await api.submitVote(debateId, titan)
        // Fetch community votes
        const votes = await api.getVotes(debateId)
        setCommunityVotes(votes)
      } catch (err) {
        console.warn('Failed to submit vote:', err)
      }
    }

    // Calculate winner based on confidence + user vote bonus
    const roundsToUse = rounds.length > 0 ? rounds : mockBattle.rounds
    const grokTotal = roundsToUse.reduce((sum, r) => sum + (r.grok?.confidence || 0), 0)
    const opusTotal = roundsToUse.reduce((sum, r) => sum + (r.opus?.confidence || 0), 0)

    const finalGrok = grokTotal + (titan === 'grok' ? 10 : 0)
    const finalOpus = opusTotal + (titan === 'opus' ? 10 : 0)

    setGrokScore(finalGrok)
    setOpusScore(finalOpus)

    setTimeout(() => {
      const battleWinner = finalGrok > finalOpus ? 'grok' : 'opus'
      setWinner(battleWinner)
      setPhase('result')
      onVote?.(titan)
      onComplete?.({
        winner: battleWinner,
        userVote: titan,
        grokScore: finalGrok,
        opusScore: finalOpus,
        debateId,
        communityVotes
      })
    }, 1500)
  }

  const getCurrentRoundData = () => rounds[currentRound] || {}

  return (
    <div className="titans-battle" ref={battleRef}>
      {/* Header */}
      <div className="battle-header">
        <div className="battle-title">⚔️ BATTLE OF THE TITANS</div>
        {battleStarted && query && <div className="battle-query">{query}</div>}
        {battleStarted && <div className="battle-phase">{phase.toUpperCase().replace(/[0-9]/g, '')}</div>}
      </div>

      {/* Query Input Phase */}
      {phase === 'input' && (
        <div className="battle-input-section">
          <form onSubmit={handleStartBattle} className="battle-form">
            <input
              type="text"
              className="battle-input"
              placeholder="Ask a question for Grok and Opus to debate..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <button type="submit" className="battle-start-btn" disabled={!query.trim()}>
              Start Battle
            </button>
          </form>
          <p className="battle-hint">
            Grok (Contrarian) vs Opus (Judge) — 3 rounds of debate. You decide the winner.
          </p>
        </div>
      )}

      {/* Intro Animation */}
      {phase === 'intro' && (
        <div className="battle-intro">
          <div className="titan-entrance grok">
            <span className="titan-icon">{TITANS.grok.icon}</span>
            <span className="titan-name">{TITANS.grok.name}</span>
          </div>
          <div className="vs-badge">VS</div>
          <div className="titan-entrance opus">
            <span className="titan-icon">{TITANS.opus.icon}</span>
            <span className="titan-name">{TITANS.opus.name}</span>
          </div>
        </div>
      )}

      {/* Battle Arena */}
      {phase !== 'intro' && phase !== 'result' && (
        <div className="battle-arena">
          {/* Grok Side */}
          <div className={`titan-panel grok ${typing.grok ? 'typing' : ''}`}>
            <div className="titan-header">
              <span className="titan-icon">{TITANS.grok.icon}</span>
              <div className="titan-info">
                <span className="titan-name">{TITANS.grok.name}</span>
                <span className="titan-role">{TITANS.grok.role}</span>
              </div>
              {getCurrentRoundData().grok?.position && (
                <span className={`position-badge ${getCurrentRoundData().grok.position.toLowerCase()}`}>
                  {getCurrentRoundData().grok.position}
                </span>
              )}
            </div>
            <div className="titan-argument">
              {typing.grok ? (
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              ) : (
                <>
                  <p>{getCurrentRoundData().grok?.argument}</p>
                  {getCurrentRoundData().grok?.finalPosition && (
                    <div className="final-call">
                      Final Call: <strong>{getCurrentRoundData().grok.finalPosition}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="confidence-bar">
              <div
                className="confidence-fill grok"
                style={{ width: typing.grok ? '0%' : `${getCurrentRoundData().grok?.confidence || 0}%` }}
              />
              <span className="confidence-value">{getCurrentRoundData().grok?.confidence || 0}%</span>
            </div>
          </div>

          {/* Center Divider */}
          <div className="battle-divider">
            <div className="round-indicator">
              ROUND {currentRound + 1}/3
            </div>
          </div>

          {/* Opus Side */}
          <div className={`titan-panel opus ${typing.opus ? 'typing' : ''}`}>
            <div className="titan-header">
              <span className="titan-icon">{TITANS.opus.icon}</span>
              <div className="titan-info">
                <span className="titan-name">{TITANS.opus.name}</span>
                <span className="titan-role">{TITANS.opus.role}</span>
              </div>
              {getCurrentRoundData().opus?.position && (
                <span className={`position-badge ${getCurrentRoundData().opus.position.toLowerCase()}`}>
                  {getCurrentRoundData().opus.position}
                </span>
              )}
            </div>
            <div className="titan-argument">
              {typing.opus ? (
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              ) : (
                <>
                  <p>{getCurrentRoundData().opus?.argument}</p>
                  {getCurrentRoundData().opus?.finalPosition && (
                    <div className="final-call">
                      Final Call: <strong>{getCurrentRoundData().opus.finalPosition}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="confidence-bar">
              <div
                className="confidence-fill opus"
                style={{ width: typing.opus ? '0%' : `${getCurrentRoundData().opus?.confidence || 0}%` }}
              />
              <span className="confidence-value">{getCurrentRoundData().opus?.confidence || 0}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Voting Phase */}
      {phase === 'voting' && !userVote && (
        <div className="voting-section">
          <h3>Who Won This Battle?</h3>
          <p>Your vote adds +10 to the final score</p>
          <div className="vote-buttons">
            <button className="vote-btn grok" onClick={() => handleVote('grok')}>
              <span className="vote-icon">{TITANS.grok.icon}</span>
              <span className="vote-name">{TITANS.grok.name}</span>
            </button>
            <button className="vote-btn opus" onClick={() => handleVote('opus')}>
              <span className="vote-icon">{TITANS.opus.icon}</span>
              <span className="vote-name">{TITANS.opus.name}</span>
            </button>
          </div>
        </div>
      )}

      {/* Calculating */}
      {userVote && phase === 'voting' && (
        <div className="calculating">
          <div className="calc-spinner"></div>
          <p>Calculating final scores...</p>
        </div>
      )}

      {/* Result */}
      {phase === 'result' && winner && (
        <div className="battle-result">
          <div className={`winner-badge ${winner}`}>
            <span className="winner-icon">{TITANS[winner].icon}</span>
            <span className="winner-text">{TITANS[winner].name} WINS</span>
          </div>
          <div className="final-scores">
            <div className={`score grok ${winner === 'grok' ? 'winner' : ''}`}>
              <span className="score-name">{TITANS.grok.name}</span>
              <span className="score-value">{grokScore}</span>
              {userVote === 'grok' && <span className="your-vote">+10 YOUR VOTE</span>}
            </div>
            <div className={`score opus ${winner === 'opus' ? 'winner' : ''}`}>
              <span className="score-name">{TITANS.opus.name}</span>
              <span className="score-value">{opusScore}</span>
              {userVote === 'opus' && <span className="your-vote">+10 YOUR VOTE</span>}
            </div>
          </div>
          <div className="result-tagline">
            {TITANS[winner].tagline}
          </div>
          {communityVotes && (
            <div className="community-votes">
              <div className="community-label">Community Vote</div>
              <div className="community-bar">
                <div
                  className="community-grok"
                  style={{ width: `${(communityVotes.grok / communityVotes.total) * 100}%` }}
                >
                  {Math.round((communityVotes.grok / communityVotes.total) * 100)}%
                </div>
                <div
                  className="community-opus"
                  style={{ width: `${(communityVotes.opus / communityVotes.total) * 100}%` }}
                >
                  {Math.round((communityVotes.opus / communityVotes.total) * 100)}%
                </div>
              </div>
              <div className="community-total">{communityVotes.total} votes</div>
            </div>
          )}
          <div className="battle-tracked">
            This battle has been recorded to the Titans Track Record
          </div>
          <button className="new-battle-btn" onClick={handleNewBattle}>
            New Battle
          </button>
        </div>
      )}

      {/* Track Record - Always visible at bottom */}
      <div className="titans-track-record-section">
        <TitanTrackRecord />
      </div>
    </div>
  )
}
